
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Thread, ThreadReply } from './types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MessageSquare, ThumbsUp } from 'lucide-react';
import ThreadReplies from './ThreadReplies';
import ReplyForm from './ReplyForm';
import ThreadsLoading from './ThreadsLoading';

const ThreadView = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchThread = async () => {
      if (!threadId) return;
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from("club_threads")
          .select(`
            *,
            profiles:user_id (id, full_name, username, avatar_url),
            replies:club_thread_replies (count),
            likes:thread_likes (count)
          `)
          .eq("id", threadId)
          .single();
          
        if (error) throw error;
        
        if (data) {
          // Check if user has liked this thread
          let threadWithLikes = { ...data };
          
          if (user) {
            const { data: userLike } = await supabase
              .from("thread_likes")
              .select("thread_id")
              .eq("thread_id", threadId)
              .eq("user_id", user.id)
              .maybeSingle();
            
            threadWithLikes = {
              ...data,
              liked: !!userLike,
              reply_count: data.replies.length,
              likes_count: data.likes.length,
              author: data.profiles
            };
          }
          
          setThread(threadWithLikes);
        }
      } catch (error) {
        console.error("Error fetching thread:", error);
        toast({
          title: "Error",
          description: "Failed to load thread",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchThread();
  }, [threadId, user]);

  const handleLikeThread = async () => {
    if (!user || !thread) {
      toast({
        title: "Authentication required",
        description: "Please sign in to like threads",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Optimistically update the UI
      setThread(prevThread => {
        if (!prevThread) return null;
        
        return {
          ...prevThread,
          liked: !prevThread.liked,
          likes_count: prevThread.liked 
            ? prevThread.likes_count - 1 
            : prevThread.likes_count + 1
        };
      });
      
      if (thread.liked) {
        // Remove the like
        await supabase
          .from("thread_likes")
          .delete()
          .eq("thread_id", thread.id)
          .eq("user_id", user.id);
      } else {
        // Add the like
        await supabase
          .from("thread_likes")
          .insert({
            thread_id: thread.id,
            user_id: user.id
          });
      }
    } catch (error) {
      console.error("Error updating like:", error);
      toast({
        title: "Error",
        description: "Failed to update like",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <ThreadsLoading />;
  }

  if (!thread) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium mb-2">Thread not found</h3>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Threads
      </Button>
      
      <Card>
        <CardHeader>
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={thread.author?.avatar_url || ""} alt="Author" />
                <AvatarFallback>
                  {thread.author?.full_name?.charAt(0) || thread.author?.username?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-xl">
                  {thread.title}
                </CardTitle>
                <div className="text-sm text-muted-foreground">
                  Posted by {thread.author?.full_name || thread.author?.username || "Anonymous"}{" "}
                  • {new Date(thread.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <Badge variant="outline">{thread.category}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-base whitespace-pre-wrap">{thread.content}</p>
        </CardContent>
        <CardFooter className="pt-2 flex justify-between border-t">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLikeThread}
              className={thread.liked ? "text-primary" : ""}
            >
              <ThumbsUp className="h-4 w-4 mr-1" />
              <span>{thread.likes_count}</span>
            </Button>
            <div className="flex items-center text-sm text-muted-foreground">
              <MessageSquare className="h-4 w-4 mr-1" />
              <span>{thread.reply_count} replies</span>
            </div>
          </div>
        </CardFooter>
      </Card>
      
      <ThreadReplies threadId={thread.id} />
      
      <ReplyForm threadId={thread.id} />
    </div>
  );
};

export default ThreadView;
